import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { ManageEnrollOperation, ManagedEnrollmentClassroomListResponse, ManagedEnrollmentProcessResponse, ManagedEnrollmentTrainingsListOrderBy, UnitTypeResponse, UserSettingsDto } from "generated/CoreApi_v1";
import React, { PropsWithChildren, useState } from "react";
import { Outlet } from "react-router";
import { IAdvFilterParams } from "views/general/AdvancedFilter";
import { EnrollmentNavigationSource, TrainingSelected } from "views/lms/enrollment/Home";

////////////////////////////////////////////////////////////////////////////////

export interface EnrollmentListFilter extends IAdvFilterParams<ManagedEnrollmentTrainingsListOrderBy> {
    addCompleted: boolean;
}

type EnrolmentListPagination = {
    page: number;
};

export enum EnrollmentExtraTimeOptions {
    Days = 0,
    Date = 1
}

export enum EnrollmentExcludeOptions {
    OnlyLatestEnrollments = 0,
    AllEnrollments = 1
}

type EnrollmentOptions = {
    enrollForcePrerequisites: boolean;
    enrollSendNotification: boolean;
    ImportedFile: boolean;
    cancelReason: AutoCompleteDataModel[];
    cancelRemarks: string | null;
    extraTimeOption: EnrollmentExtraTimeOptions;
    aditionalDays: number;
    untilDate: Date | null;
    revokeReason: string;
    undoRevokeReason: string;
    reEnrollRevokeApprovalPrevVersion: boolean;
    reEnrollReuseComponent: boolean;
    excludeOption: EnrollmentExcludeOptions;
    excludeReason: string;
    excludeSendNotification: boolean;
};

type EnrollmentContextType = {
    filter: EnrollmentListFilter;
    setFilter: (data: Partial<EnrollmentListFilter>) => void;
    resetFilter: () => void;
    editNavigation: EnrollmentNavigationSource[];
    setEditNavigation: (data: EnrollmentNavigationSource[]) => void;
    pagination: EnrolmentListPagination;
    setPagination: (data: EnrolmentListPagination) => void;
    training?: TrainingSelected;
    setTraining: React.Dispatch<React.SetStateAction<TrainingSelected | undefined>>;
    classroom?: ManagedEnrollmentClassroomListResponse;
    setClassroom: React.Dispatch<React.SetStateAction<ManagedEnrollmentClassroomListResponse | undefined>>;
    operation: ManageEnrollOperation | undefined;
    setOperation: React.Dispatch<React.SetStateAction<ManageEnrollOperation | undefined>>;
    options: EnrollmentOptions;
    setOptions: React.Dispatch<React.SetStateAction<EnrollmentOptions>>;
    result: ManagedEnrollmentProcessResponse | undefined;
    setResult: React.Dispatch<React.SetStateAction<ManagedEnrollmentProcessResponse | undefined>>;
    unitTypes: UnitTypeResponse[] | undefined;
    setUnitTypes: React.Dispatch<React.SetStateAction<UnitTypeResponse[] | undefined>>;
    userSettings: UserSettingsDto | undefined;
    setUserSettings: React.Dispatch<React.SetStateAction<UserSettingsDto | undefined>>;
    clearSelection: () => void;
};

const EnrollmentContext = React.createContext<EnrollmentContextType>(undefined!);

////////////////////////////////////////////////////////////////////////////////

export const useEnrollmentManagementContext = () => React.useContext(EnrollmentContext);

function getEnrollOptions(): EnrollmentOptions {
    return {
        enrollForcePrerequisites: true,
        enrollSendNotification: true,
        ImportedFile: false,
        cancelReason: [],
        cancelRemarks: "",
        extraTimeOption: EnrollmentExtraTimeOptions.Days,
        aditionalDays: 3,
        untilDate: null,
        revokeReason: "",
        undoRevokeReason: "",
        reEnrollRevokeApprovalPrevVersion: false,
        reEnrollReuseComponent: false,
        excludeOption: EnrollmentExcludeOptions.OnlyLatestEnrollments,
        excludeReason: "",
        excludeSendNotification: true
    };
}

////////////////////////////////////////////////////////////////////////////////

export const EnrollmentManagementWrapper: React.FC<PropsWithChildren> = () => {
    const [pagination, setPagination] = React.useState<EnrolmentListPagination>({ page: 1 });
    const [editNavigation, setEditNavigation] = React.useState<EnrollmentNavigationSource[]>([]);
    const [training, setTraining] = React.useState<TrainingSelected>();
    const [classroom, setClassroom] = useState<ManagedEnrollmentClassroomListResponse>();
    const [operation, setOperation] = useState<ManageEnrollOperation>();
    const [result, setResult] = useState<ManagedEnrollmentProcessResponse>();
    const [options, setOptions] = useState<EnrollmentOptions>(getEnrollOptions());
    const [unitTypes, setUnitTypes] = useState<UnitTypeResponse[]>();
    const [userSettings, setUserSettings] = useState<UserSettingsDto>();

    const [filter, setFilter] = React.useState<EnrollmentListFilter>({
        searchText: "",
        addCompleted: false,
        advFilters: [],
        order: ManagedEnrollmentTrainingsListOrderBy.Name,
    });

    ///
    const resetFilter = () => {
        setFilter(state => ({ ...state, searchText: "" }));
        setPagination({ page: 1 });
    };

    ///
    const clearSelection = () => {
        setTraining(undefined);
        setClassroom(undefined);
        setOperation(undefined);
        setOptions(getEnrollOptions());
    };

    ///
    return (
        <EnrollmentContext.Provider
            value={
                {
                    filter,
                    setFilter: (data: Partial<EnrollmentListFilter>) => setFilter(state => ({ ...state, ...data })),
                    resetFilter,
                    editNavigation, setEditNavigation,
                    pagination, setPagination: (data: EnrolmentListPagination) => setPagination(data),
                    training: training,
                    setTraining: setTraining,
                    classroom,
                    setClassroom,
                    operation,
                    setOperation,
                    options,
                    setOptions,
                    result,
                    setResult,
                    unitTypes,
                    setUnitTypes,
                    userSettings,
                    setUserSettings,
                    clearSelection
                }
            }
        >
            <Outlet />
        </EnrollmentContext.Provider>
    );
};
